import { useState, useEffect, useCallback } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, StatTile, SectionLabel, Pill, DataTable } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/data-trust) ───────────────────────────────────
interface LocationFreshness {
  id: string
  name: string
  tracked_rivals: number
  last_collected_at: string | null
}
interface DataTrust {
  coverage: {
    locations_monitored: number
    tracked_rivals: number
    blocked_rivals: number
    skus_tracked: number
    brands_tracked: number
  }
  freshness: {
    last_collected_at: string | null
    rivals_with_data: number
    rivals_total: number
  }
  locations: LocationFreshness[]
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtInt = (n: number) => n.toLocaleString('en-US')
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
function relTime(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const mins = Math.floor((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`
  const days = Math.floor(hrs / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// ─── Evergreen explainer copy (customer-terms, no source/methodology) ─────────
// These describe trust & freshness in customer language — never the real
// technical pipeline. No data platform is ever named. Numbers on this page that
// must be real come from /api/v1/data-trust, NOT from here.
const PIPELINE_STEPS = [
  { num: '01', title: 'Discovery', body: 'Licensed dispensaries are identified from the California DCC license database. No competitor enters your set without a verified license.', badge: 'Nightly scan', tone: 'accent' as const },
  { num: '02', title: 'Collection', body: 'Menu data is gathered from publicly available cannabis market sources — pricing, SKU details, sale flags, and discount labels, exactly as a customer would see them.', badge: '2–5 AM Pacific', tone: 'accent' as const },
  { num: '03', title: 'Normalization', body: 'Product names are matched across dispensaries so the same item resolves to one SKU for accurate cross-market comparison.', badge: 'AI-assisted', tone: 'warm' as const },
  { num: '04', title: 'Diff & Alert', body: 'Each new collection is compared to the prior one. Price changes, new SKUs, removed products, and promo starts generate alerts.', badge: 'Real-time events', tone: 'danger' as const },
  { num: '05', title: 'Redundancy', body: 'A parallel fallback method runs independently. If primary collection is interrupted, fallback keeps your data from going stale.', badge: 'Always-on backup', tone: 'neutral' as const },
]

const COV_ROWS: { field: string; yes: boolean; note: string }[] = [
  { field: 'Current price per SKU', yes: true, note: 'Live price as shown to customers today' },
  { field: 'On-sale flag & original price', yes: true, note: 'Detects active discounts and sale depth' },
  { field: 'Discount label text', yes: true, note: 'e.g. "20% off", "BOGO", "Happy Hour"' },
  { field: 'Price vs. prior day (delta)', yes: true, note: 'Computed by the CannaSpy diff engine' },
  { field: 'Product brand & category', yes: true, note: 'Flower, Vapes, Pre-rolls, Edibles, Concentrates' },
  { field: 'Strain name & genetics', yes: true, note: 'Indica / Sativa / Hybrid + strain name where available' },
  { field: 'Lab-verified THC/CBD values', yes: true, note: 'In mg per item when provided by the dispensary' },
  { field: 'New & discontinued products', yes: true, note: 'Detected each run by comparison to the prior snapshot' },
  { field: 'Promotional schedule', yes: true, note: 'Happy hours, daily brand deals, first-time offers' },
  { field: 'Business hours & open status', yes: true, note: 'Current-day hours and live open/closed flag' },
  { field: 'Online ordering availability', yes: true, note: 'Whether delivery or pickup is currently active' },
  { field: 'Transaction-level sales data', yes: false, note: 'Not collected — CannaSpy reads public menus only' },
  { field: 'Customer PII or loyalty data', yes: false, note: 'Not collected — no access to internal systems' },
  { field: 'Internal inventory quantities', yes: false, note: 'Not publicly accessible; in-stock status only' },
]

const FAQS = [
  { q: 'Where does the pricing data come from?', a: 'CannaSpy aggregates pricing, product, and promotional data from publicly available cannabis market sources. Our data collection infrastructure is proprietary — what matters to you is that pricing and menu data is updated daily and covers the licensed dispensaries in your competitive set.' },
  { q: 'Is this legal? Are you monitoring without permission?', a: 'CannaSpy collects only publicly available data — information any consumer, competitor, or third party can access through normal browsing. We do not bypass authentication, access private systems, or collect data that requires a login. This is the same approach used by price-comparison services, market-research firms, and competitive-intelligence tools across every industry.' },
  { q: 'How fresh is the data? When was the last update?', a: 'Every tracked dispensary is refreshed daily between 2:00–5:00 AM Pacific. By the time you open your dashboard in the morning, the prior day\'s prices are already reflected. Price changes detected overnight appear as alerts shortly after the collection run completes. The live collection status for your account is shown at the top of this page.' },
  { q: 'What happens if a competitor moves to a different menu platform?', a: 'Our data collection is designed to be platform-agnostic. We track what dispensaries make publicly available, regardless of which menu system they use. Moving platforms does not meaningfully affect our coverage of that competitor — we maintain a fallback method specifically for this scenario.' },
  { q: 'How accurate is the product normalization?', a: 'Product-name normalization uses an AI-assisted matching system tuned to the California cannabis market. Matching accuracy across identical products at different dispensaries is consistently high, and low-confidence matches are flagged with a reduced-confidence indicator in the price matrix.' },
  { q: 'Can a competitor see that CannaSpy is monitoring them?', a: 'No. CannaSpy reads the same public menu data any consumer sees when browsing. There is no tracking or identifiable signal that distinguishes CannaSpy from ordinary traffic, so competitors have no way to detect that they are being monitored.' },
  { q: 'What if the data shows a price that looks wrong?', a: 'Every data point carries a timestamp. If a price looks inconsistent with what you know, flag it and our team re-verifies against the live source within 24 hours. Confirmed corrections are noted in the data provenance log.' },
]

// ─── Small building blocks (composed from primitives) ────────────────────────
function CoverageTile({ value, label, sub, tone = 'accent' }: {
  value: React.ReactNode; label: string; sub: string; tone?: 'accent' | 'default'
}) {
  return (
    <Card variant="sm" style={{ padding: '16px 18px', minWidth: 0 }}>
      <StatTile size="sm" boxed={false} tone={tone} value={value} label={label} />
      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.4 }}>{sub}</div>
    </Card>
  )
}

const TONE_BADGE: Record<string, React.CSSProperties> = {
  accent: { background: 'var(--accent-soft)', color: 'var(--accent)' },
  warm: { background: 'var(--warm-soft)', color: 'var(--warm)' },
  danger: { background: 'var(--danger-soft)', color: 'var(--danger)' },
  neutral: { background: 'var(--surface-3)', color: 'var(--text-3)' },
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function DataTrust() {
  const authFetch = useAuthFetch()
  const [data, setData] = useState<DataTrust | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/data-trust`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: DataTrust }) => setData(d.data))
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch])

  useEffect(() => { load() }, [load])

  const cov = data?.coverage
  const fresh = data?.freshness
  const lastRel = relTime(fresh?.last_collected_at ?? null)
  const hasData = !!fresh?.last_collected_at

  const locColumns: Column<LocationFreshness>[] = [
    {
      key: 'name', header: 'Location', mono: false,
      render: (l) => <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{l.name}</span>,
    },
    {
      key: 'tracked_rivals', header: 'Rivals tracked', align: 'right',
      render: (l) => l.tracked_rivals > 0
        ? <span style={{ color: 'var(--text-1)' }}>{fmtInt(l.tracked_rivals)}</span>
        : <span style={{ color: 'var(--text-3)' }}>0</span>,
    },
    {
      key: 'last_collected_at', header: 'Last updated', align: 'right', mono: false,
      render: (l) => l.last_collected_at
        ? (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-1)' }}>
            {fmtDateTime(l.last_collected_at)}
            <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>· {relTime(l.last_collected_at)}</span>
          </span>
        )
        : l.tracked_rivals > 0
          ? <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--warm)' }}>first refresh in progress</span>
          : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 14, overflow: 'hidden' }}>
      {/* Topbar */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>Data Health</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>HOW WE COLLECT DATA &amp; WHY YOU CAN RELY ON IT</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {hasData ? (
            <Pill tone="accent" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Data as of {fmtDateTime(fresh!.last_collected_at)}
            </Pill>
          ) : loading ? (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>Checking collection status…</span>
          ) : null}
        </div>
      </div>

      {/* Scroll body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', minHeight: 0 }}>

        {/* HERO */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 8 }}>Data confidence</div>
          <div style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 10 }}>
            You are looking at real data.<br />Not estimates. Not surveys.
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 640 }}>
            CannaSpy collects pricing, product, and promotional data directly from publicly available cannabis market sources — the same information any consumer can access by browsing your competitors. Every price in your dashboard reflects what a customer would see if they walked in today.
          </div>
        </div>

        {/* REAL COVERAGE + FRESHNESS STRIP */}
        <SectionLabel style={{ marginBottom: 12 }}>Your coverage &amp; freshness</SectionLabel>
        {error ? (
          <Card variant="sm" style={{ padding: '18px 20px', marginBottom: 28, borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load your live coverage.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12, marginBottom: 28 }}>
            <CoverageTile
              value={loading && !cov ? '—' : fmtInt(cov?.locations_monitored ?? 0)}
              label="Locations monitored"
              sub="Your active storefronts under continuous competitive monitoring"
            />
            <CoverageTile
              value={loading && !cov ? '—' : fmtInt(cov?.tracked_rivals ?? 0)}
              label="Rivals tracked"
              sub="Competitors you actively track"
            />
            <CoverageTile
              value={loading && !fresh ? '—' : `${fmtInt(fresh?.rivals_with_data ?? 0)} / ${fmtInt(fresh?.rivals_total ?? 0)}`}
              label="Rivals with live data"
              sub="Tracked rivals with a completed menu collection on file"
            />
            <CoverageTile
              value={loading && !cov ? '—' : fmtInt(cov?.skus_tracked ?? 0)}
              label="SKUs tracked"
              sub={cov ? `Across ${fmtInt(cov.brands_tracked)} brands on your rivals' live menus` : "Distinct products on your rivals' menus"}
            />
            <CoverageTile
              value={loading && !data ? '—' : hasData ? (lastRel ?? 'Recent') : 'Pending'}
              label="Last refresh"
              tone={hasData ? 'accent' : 'default'}
              sub={hasData ? `Most recent collection completed ${fmtDateTime(fresh!.last_collected_at)}` : 'First collection run for your rivals is in progress'}
            />
          </div>
        )}

        {/* PIPELINE (evergreen, customer-terms) */}
        <SectionLabel style={{ marginBottom: 12 }}>How your data is collected</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 8, marginBottom: 28 }}>
          {PIPELINE_STEPS.map((step) => (
            <Card key={step.num} variant="sm" style={{ padding: '14px 16px', minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, color: 'var(--accent)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                {step.num}{step.num !== '05' && <span style={{ opacity: 0.5 }}>→</span>}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>{step.title}</div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', lineHeight: 1.5 }}>{step.body}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 3, marginTop: 8, display: 'inline-block', ...TONE_BADGE[step.tone] }}>{step.badge}</div>
            </Card>
          ))}
        </div>

        {/* REAL PER-LOCATION FRESHNESS */}
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)' }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>Coverage by location</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>
              {data ? `${fmtInt(data.locations.length)} monitored` : ''}
            </div>
          </div>
          <div style={{ padding: '4px 8px' }}>
            <DataTable
              columns={locColumns}
              rows={data?.locations ?? []}
              rowKey={(l) => l.id}
              empty={loading
                ? <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Pulling your latest collection status…</span>
                : <span>No active locations yet. Add a location to start monitoring your competitive set.</span>}
            />
          </div>
        </Card>

        {/* COVERAGE FIELDS (evergreen) */}
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)' }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2}><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>What data fields are collected per dispensary</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <thead>
              <tr>
                {['Data field', 'Collected', 'Notes'].map((h, i) => (
                  <th key={h} style={{ padding: '7px 12px', textAlign: i === 1 ? 'center' : 'left', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-3)', background: 'var(--surface-3)', borderBottom: '1px solid var(--border-2)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COV_ROWS.map((row, i) => (
                <tr key={row.field}>
                  <td style={{ padding: '8px 12px', borderBottom: i === COV_ROWS.length - 1 ? 'none' : '1px solid var(--border)', fontWeight: 600, color: 'var(--text-1)' }}>{row.field}</td>
                  <td style={{ padding: '8px 12px', borderBottom: i === COV_ROWS.length - 1 ? 'none' : '1px solid var(--border)', textAlign: 'center' }}>
                    {row.yes ? (
                      <span style={{ width: 16, height: 16, borderRadius: 4, background: 'var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} style={{ width: 10, height: 10 }}><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                    ) : (
                      <span style={{ width: 16, height: 16, borderRadius: 4, background: 'var(--surface-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={3} style={{ width: 10, height: 10 }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: i === COV_ROWS.length - 1 ? 'none' : '1px solid var(--border)', color: 'var(--text-2)' }}>{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* FAQ (evergreen) */}
        <SectionLabel style={{ marginBottom: 12 }}>Common questions about data quality</SectionLabel>
        {FAQS.map((faq, i) => (
          <Card key={i} variant="sm" style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
            <div
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{faq.q}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16, color: 'var(--text-3)', flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {openFaq === i && (
              <div style={{ padding: '0 18px 14px', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.7, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                {faq.a}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
